import Link from 'next/link';
import { CSSProperties } from 'react';
import { VariantProps } from 'tailwind-variants';

import { utilContrastingColor } from '@/fo/util/contrasting-color';

import { styles } from './UiKitLabelBase.styles';

export type Color = VariantProps<typeof styles>['color'];
export type Variant = VariantProps<typeof styles>['variant'];
export type Size = VariantProps<typeof styles>['size'];

type NativeElement = React.HTMLAttributes<HTMLDivElement>;
type LinkElement = React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type BaseProps = {
  as?: string;
  className?: string;
  children?: React.ReactNode;
  variant?: Variant;
  size?: Size;
  color?: Color;
  customColor?: string;
  textColor?: string;
  uppercase?: boolean;
  style?: React.CSSProperties;
};

export type DivProps = {
  as?: 'div';
  onClick?: NativeElement['onClick'];
};

export type LinkProps = {
  as: 'link';
  onClick?: LinkElement['onClick'];
  href: string;
  target?: LinkElement['target'];
  rel?: LinkElement['rel'];
};

type PropsAs = DivProps | LinkProps;

export type UiKitLabelBaseProps = BaseProps & PropsAs;

export function UiKitLabelBase({
  className,
  children,
  color,
  customColor,
  variant,
  size,
  uppercase = false,
  textColor,
  ...props
}: UiKitLabelBaseProps) {
  const baseClasses = `${className ? className : ''} ${uppercase ? 'uppercase' : ''}`;
  const customTextColor =
    (textColor || (customColor && utilContrastingColor(customColor))) ??
    undefined;

  const style = {
    '--color-label-base-bg': customColor,
    '--color-label-base-text': customTextColor
  } as CSSProperties;

  const slots = styles({
    color: customColor ? 'custom' : color,
    variant,
    size
  });

  if (props.as === 'link') {
    const { href, ...rest } = props;

    return (
      <Link
        href={href}
        className={slots.base({ class: baseClasses, link: true })}
        style={style}
        {...rest}
        as={undefined}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      className={slots.base({ class: baseClasses })}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
}
